import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  DialogTitle,
  Input,
  Link,
  Modal,
  ModalClose,
  ModalDialog,
  Sheet,
  Stack,
  Typography,
} from '@mui/joy';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../../../lib/api';
import type {
  Checkpoint,
  CheckpointComparison,
} from '../../../types/websiteWorkspace';
import { apiMessage } from './workspaceLogic';

export default function CheckpointPanel({
  projectId,
  checkpoints,
}: {
  projectId: string;
  checkpoints: Checkpoint[];
}) {
  const cache = useQueryClient();
  const [label, setLabel] = useState('');
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [reviewLink, setReviewLink] = useState('');
  const [notice, setNotice] = useState('');
  const root = `/api/website-builder/projects/${projectId}`;
  const refresh = () => {
    cache.invalidateQueries({ queryKey: ['website-workbench', projectId] });
    cache.invalidateQueries({
      queryKey: ['website-builder-project', projectId],
    });
    cache.invalidateQueries({ queryKey: ['website-builder-page', projectId] });
  };
  const create = useMutation({
    mutationFn: () =>
      api.post(`${root}/checkpoints/`, {
        label: label.trim() || 'Review checkpoint',
      }),
    onSuccess: () => {
      setLabel('');
      refresh();
    },
  });
  const comparison = useQuery({
    queryKey: ['website-checkpoint', projectId, selectedId],
    queryFn: async () =>
      (
        await api.get<CheckpointComparison>(
          `${root}/checkpoints/${selectedId}/`,
        )
      ).data,
    enabled: selectedId !== null,
    staleTime: 0,
  });
  const restore = useMutation({
    mutationFn: () =>
      api.post(`${root}/checkpoints/${selectedId}/`, {
        base_digest: comparison.data?.current_digest,
      }),
    onSuccess: () => {
      setSelectedId(null);
      setNotice(
        'Checkpoint restored to draft. A backup was created first. Refresh the preview, review legal pages again, and publish when ready.',
      );
      refresh();
    },
  });
  const share = useMutation({
    mutationFn: async ({ id, revoke }: { id: number; revoke?: boolean }) =>
      revoke
        ? (await api.delete(`${root}/checkpoints/${id}/share/`)).data
        : (
            await api.post<{ path: string; expires_at: string }>(
              `${root}/checkpoints/${id}/share/`,
              {},
            )
          ).data,
    onSuccess: (data) => {
      setReviewLink(
        data &&
          typeof data === 'object' &&
          'path' in data &&
          'expires_at' in data &&
          data.expires_at
          ? `${window.location.origin}${data.path}`
          : '',
      );
      refresh();
    },
  });
  return (
    <Stack spacing={2}>
      <Typography level="h3">Draft history & checkpoints</Typography>
      <Typography>
        Create a named checkpoint before a major change. Each includes page
        content, photos, structured data, shared templates and assets. Restoring
        creates a backup and never publishes.
      </Typography>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Input
          value={label}
          onChange={(event) => setLabel(event.target.value)}
          placeholder="Checkpoint name"
          slotProps={{ input: { maxLength: 180 } }}
        />
        <Button loading={create.isPending} onClick={() => create.mutate()}>
          Create checkpoint
        </Button>
      </Box>
      {(create.isError || share.isError) && (
        <Alert color="danger">
          {apiMessage(
            create.error || share.error,
            'The action failed. Please retry.',
          )}
        </Alert>
      )}
      {notice && <Alert color="success">{notice}</Alert>}
      {reviewLink && (
        <Alert color="neutral">
          <Stack>
            <Typography level="title-sm">
              Review link · expires in 24 hours
            </Typography>
            <Link
              href={reviewLink}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ overflowWrap: 'anywhere' }}
            >
              {reviewLink}
            </Link>
            <Typography level="body-sm">
              Only signed-in teammates in your organization can open this frozen
              draft.
            </Typography>
          </Stack>
        </Alert>
      )}
      {!checkpoints.length && (
        <Alert color="neutral">
          No checkpoints yet. Create one to preserve your current draft.
        </Alert>
      )}
      {checkpoints.map((row) => (
        <Sheet
          key={row.id}
          variant="outlined"
          sx={{ p: 2, borderRadius: 'md' }}
        >
          <Stack spacing={1}>
            <Typography level="title-md">{row.label}</Typography>
            <Typography level="body-sm">
              {row.created_by} · {new Date(row.created_at).toLocaleString()} ·{' '}
              {row.page_count} pages
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                size="sm"
                variant="outlined"
                onClick={() => {
                  restore.reset();
                  setSelectedId(row.id);
                }}
              >
                Compare / restore
              </Button>
              <Button
                size="sm"
                variant="plain"
                disabled={share.isPending}
                onClick={() => share.mutate({ id: row.id })}
              >
                Create teammate review link
              </Button>
              {row.share_expires_at &&
                new Date(row.share_expires_at) > new Date() && (
                  <Button
                    size="sm"
                    variant="plain"
                    color="danger"
                    disabled={share.isPending}
                    onClick={() => share.mutate({ id: row.id, revoke: true })}
                  >
                    Revoke link
                  </Button>
                )}
            </Box>
          </Stack>
        </Sheet>
      ))}
      <Modal
        open={selectedId !== null}
        onClose={restore.isPending ? undefined : () => setSelectedId(null)}
      >
        <ModalDialog sx={{ width: 1000, maxWidth: '96vw', overflowY: 'auto' }}>
          <ModalClose disabled={restore.isPending} />
          <DialogTitle>Compare checkpoint with current draft</DialogTitle>
          {comparison.isLoading && <Typography>Loading comparison…</Typography>}
          {comparison.isError && (
            <Alert color="danger">
              Could not compare this checkpoint.{' '}
              <Button onClick={() => comparison.refetch()}>Retry</Button>
            </Alert>
          )}
          {comparison.data && (
            <Stack spacing={2}>
              <Typography>
                {comparison.data.label} · {comparison.data.created_by}
              </Typography>
              <Typography>
                Shared changes:{' '}
                {comparison.data.shared_fields.join(', ') || 'none'}
                {comparison.data.assets_changed ? ' · assets changed' : ''}
              </Typography>
              {comparison.data.pages.map((page) => (
                <Box key={page.id} component="details">
                  <Typography
                    component="summary"
                    level="title-sm"
                    sx={{ cursor: 'pointer' }}
                  >
                    {page.title}{' '}
                    <Chip size="sm">{page.fields.length} changed fields</Chip>
                  </Typography>
                  {page.fields.map((field) => (
                    <Box key={field} sx={{ mt: 1 }}>
                      <Typography level="title-sm">{field}</Typography>
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                          gap: 1,
                        }}
                      >
                        {(['before', 'after'] as const).map((side) => (
                          <Sheet
                            key={side}
                            variant="soft"
                            sx={{ p: 1, minWidth: 0 }}
                          >
                            <Typography level="body-xs">
                              {side === 'before'
                                ? 'Checkpoint'
                                : 'Current draft'}
                            </Typography>
                            <Box
                              component="pre"
                              sx={{
                                whiteSpace: 'pre-wrap',
                                overflowWrap: 'anywhere',
                                maxHeight: 240,
                                overflow: 'auto',
                                fontSize: 12,
                              }}
                            >
                              {typeof page[side][field] === 'string'
                                ? String(page[side][field])
                                : JSON.stringify(page[side][field], null, 2)}
                            </Box>
                          </Sheet>
                        ))}
                      </Box>
                    </Box>
                  ))}
                </Box>
              ))}
              {!comparison.data.can_restore && (
                <Alert color="warning">
                  Pages were added or removed since this checkpoint. Review its
                  content here; full restore is unavailable so the current page
                  inventory is preserved.
                </Alert>
              )}
              {restore.isError && (
                <Alert color="danger">
                  {apiMessage(
                    restore.error,
                    'Could not restore. Refresh the comparison and try again.',
                  )}
                </Alert>
              )}
              <Button
                color="warning"
                loading={restore.isPending}
                disabled={!comparison.data.can_restore}
                onClick={() => {
                  if (
                    window.confirm(
                      'Replace the saved draft with this checkpoint? We will back up the current draft first. Live pages stay unchanged; legal pages need renewed approval.',
                    )
                  )
                    restore.mutate();
                }}
              >
                Restore this checkpoint to draft
              </Button>
            </Stack>
          )}
        </ModalDialog>
      </Modal>
    </Stack>
  );
}
